import { motion } from "framer-motion";
import { Play, Music, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type MediaItem = {
  id: string;
  title: string;
  platform: "youtube" | "spotify";
  cover?: string;
};

const mediaItems: MediaItem[] = [
  {
    id: "8OGBuOqRq34",
    title: "OT-SECURITY IN DER GEBÄUDEAUTOMATION",
    platform: "youtube",
    cover: "/podcast-expert-talk-ot-security.jpg",
  },
  {
    id: "88wgcJg9S6k",
    title: "RADIO OT",
    platform: "youtube",
    cover: `https://img.youtube.com/vi/88wgcJg9S6k/maxresdefault.jpg`,
  },
  {
    id: "285KC5fCi9epUHFNsT8btR",
    title: "RADIO OT",
    platform: "spotify",
    cover: "/podcast-radio-ot-spotify.png",
  },
  {
    id: "0oS20cfp6loGrBmMYt2dpA",
    title: "OT SECURITY MADE SIMPLE",
    platform: "spotify",
    cover: "/podcast-ot-security-made-simple.png",
  },
  {
    id: "1nRZv2EUvlpY1yByR3cLyX",
    title: "OT SECURITY MADE SIMPLE",
    platform: "spotify",
    cover: "/podcast-bacnet-sc-ot-security-made-simple.jpg",
  },
  {
    id: "fgQ49aQuyp0",
    title: "SECURING INDUSTRY",
    platform: "youtube",
    cover: `https://img.youtube.com/vi/fgQ49aQuyp0/maxresdefault.jpg`,
  },
];

const PodcastBand = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [consentGiven, setConsentGiven] = useState<Record<string, boolean>>({});
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);

  const loopItems = [...mediaItems, ...mediaItems, ...mediaItems];

  const pauseAutoScroll = (resumeAfterMs = 0) => {
    pausedRef.current = true;
    if (resumeTimerRef.current) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    if (resumeAfterMs > 0) {
      resumeTimerRef.current = window.setTimeout(() => {
        pausedRef.current = false;
      }, resumeAfterMs);
    }
  };

  const resumeAutoScroll = () => {
    if (resumeTimerRef.current) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    pausedRef.current = false;
  };

  const scrollByOffset = (offset: number) => {
    pauseAutoScroll(4000);
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (activeVideo) return;

    let rafId = 0;
    let last = performance.now();
    const speed = 45;

    let retryTimer: number | null = null;
    const setLoopStart = () => {
      const third = el.scrollWidth / 3;
      if (third > 0 && el.scrollLeft < third / 2) {
        el.scrollLeft = third;
      }
      return third > 0;
    };

    const startLoop = () => {
      if (!setLoopStart()) {
        retryTimer = window.setTimeout(startLoop, 100);
        return;
      }

      const tick = (now: number) => {
        const dt = (now - last) / 1000;
        last = now;
        if (!pausedRef.current && el) {
          el.scrollLeft += speed * dt;
        }
        const third = el.scrollWidth / 3;
        if (third > 0) {
          if (el.scrollLeft >= third * 2) {
            el.scrollLeft -= third;
          } else if (el.scrollLeft <= 0) {
            el.scrollLeft += third;
          }
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    startLoop();

    const handleVisibility = () => {
      if (!document.hidden) {
        last = performance.now();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      if (retryTimer) window.clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (resumeTimerRef.current) {
        window.clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    };
  }, [activeVideo]);

  const giveConsent = (id: string) => {
    pauseAutoScroll();
    setConsentGiven((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mt-16 relative"
    >
      <p className="text-secondary font-medium text-sm tracking-widest uppercase mb-6">
        Podcasts
      </p>

      <button
        type="button"
        onClick={() => scrollByOffset(-360)}
        className="hidden md:flex absolute left-2 top-[calc(50%+1.5rem)] -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-border shadow-lg items-center justify-center text-foreground hover:bg-background transition-colors"
        aria-label="Vorherige Podcasts"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={() => scrollByOffset(360)}
        className="hidden md:flex absolute right-2 top-[calc(50%+1.5rem)] -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background/80 backdrop-blur border border-border shadow-lg items-center justify-center text-foreground hover:bg-background transition-colors"
        aria-label="Nächste Podcasts"
      >
        <ChevronRight size={20} />
      </button>

      <p className="md:hidden text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
        <ChevronLeft size={14} />
        Wischen zum Navigieren
        <ChevronRight size={14} />
      </p>

      <div className="relative -mx-6 px-6">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10" />

        <div
          ref={scrollRef}
          onMouseEnter={() => pauseAutoScroll()}
          onMouseLeave={() => resumeAutoScroll()}
          onTouchStart={() => pauseAutoScroll(4000)}
          onWheel={() => pauseAutoScroll(4000)}
          className="flex gap-6 pb-4 overflow-x-auto scrollbar-hide touch-pan-x overscroll-x-contain"
        >
          {loopItems.map((item, idx) => {
            const loaded = consentGiven[item.id];
            const isVideoPlaying = activeVideo === item.id;
            return (
              <div
                key={`${item.id}-${idx}`}
                className={`shrink-0 rounded-lg overflow-hidden border border-border bg-card ${
                  loaded ? "w-[min(560px,90vw)]" : "w-[300px] md:w-[340px]"
                } transition-[width] duration-500`}
              >
                {!loaded ? (
                  <div className="relative">
                    <div className="relative aspect-video bg-muted overflow-hidden">
                      {item.cover && (
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
                        {item.platform === "spotify" ? (
                          <Music className="w-3 h-3" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        {item.platform === "spotify" ? "Spotify" : "YouTube"}
                      </div>
                      <p className="absolute bottom-3 left-3 right-3 text-white text-sm font-semibold drop-shadow">
                        {item.title}
                      </p>
                    </div>
                    <div className="px-3 py-2 flex items-center gap-3">
                      <p className="text-[10px] text-muted-foreground leading-snug flex-1 min-w-0">
                        <ShieldCheck className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                        Mit Klick stimmen Sie zu, dass Inhalte von{" "}
                        <span className="font-medium text-foreground">
                          {item.platform === "spotify" ? "Spotify" : "YouTube"}
                        </span>{" "}
                        geladen werden.{" "}
                        <button
                          onClick={() => window.dispatchEvent(new CustomEvent("open-datenschutz"))}
                          className="underline hover:text-foreground transition-colors"
                        >
                          Datenschutz
                        </button>
                      </p>
                      <button
                        onClick={() => giveConsent(item.id)}
                        className="shrink-0 inline-flex items-center justify-center bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs font-medium hover:bg-primary/90 transition-colors"
                      >
                        Inhalt laden
                      </button>
                    </div>
                  </div>
                ) : item.platform === "spotify" ? (
                  <iframe
                    src={`https://open.spotify.com/embed/episode/${item.id}?utm_source=generator&theme=0`}
                    title={item.title}
                    width="100%"
                    height="352"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    className="border-0"
                  />
                ) : isVideoPlaying ? (
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${item.id}?autoplay=1&rel=0`}
                      title={item.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setActiveVideo(item.id)}
                    className="relative w-full aspect-video group cursor-pointer"
                    aria-label={`${item.title} abspielen`}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${item.id}/maxresdefault.jpg`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-primary/90 group-hover:bg-primary flex items-center justify-center transition-colors shadow-lg">
                        <Play className="w-7 h-7 text-primary-foreground ml-1" />
                      </div>
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default PodcastBand;
